/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import EditableText from '@/components/editable-text';
import ToggleSwitch from '@/components/toggle-switch';
import PermissionGate from '@/components/permission-gate';
import { toWebSafeFormat } from '@/utilities/strings';
import { MerchantContext } from '@/contexts/merchant-context';
import { TelemetryContext } from '@/contexts/telemetry-context';
import { getStaticCdnPath } from '@/utilities/static-build';
import { PERMISSIONS_ENUM } from '@/utilities/rbac/constants';

export default function EditableList({
  inputSelectorId,
  onChange,
  items,
  maxLength,
  switchesEnabled,
  switchesControlled,
  telemetryOnChangeEvent,
  telemetryToggleOffEvent,
  ariaLabel,
}) {
  const [listItems, setListItems] = useState([]);

  // Clone the items if the item list changes. This is necessary because the items list
  // is an array of objects, so assigning it to listItems just creates a reference
  useEffect(() => {
    const newArray = [];
    items?.forEach((item) => {
      newArray.push({ ...item });
    });
    setListItems(newArray);
  }, [items]);

  const { isPaymentMethodEnabled, homeCountryCode } = useContext(MerchantContext);
  const telemetryContext = useContext(TelemetryContext);

  const toggleEnabled = (index) => {
    const updatedItems = [...listItems];
    updatedItems[index].enabled = !updatedItems[index].enabled;
    setListItems(updatedItems);
    onChange(updatedItems);
    if (telemetryToggleOffEvent && !updatedItems[index].enabled) {
      telemetryContext?.sendEvent(telemetryToggleOffEvent);
    }
  };

  const keyDownToggleEnabled = (event, index) => {
    if (event.key === 'Enter') {
      toggleEnabled(index);
    }
  };

  const textChangeMade = (newChangeValue, index) => {
    const updatedItems = listItems;
    updatedItems[index].title = newChangeValue;
    setListItems(updatedItems);
    onChange(updatedItems);
    if (telemetryOnChangeEvent) {
      telemetryContext?.sendEvent(telemetryOnChangeEvent);
    }
  };

  const onSwitchChange = (checked, index) => {
    const updatedItems = listItems;
    updatedItems[index].additionalToggleValue = checked;
    const { internationalPaymentRequirements } = updatedItems[index];
    if (internationalPaymentRequirements) {
      updatedItems[index].internationalPaymentRequirements[homeCountryCode] = checked;
    } else {
      updatedItems[index].internationalPaymentRequirements = { [homeCountryCode]: checked };
    }
    setListItems(updatedItems);
    onChange(updatedItems);
  };

  return (
    <>
      <div className="list" id={inputSelectorId}>
        {listItems && listItems.map((item, index) => (
          <div key={`${inputSelectorId}-${toWebSafeFormat(item.code)}`} className="list-item">
            <div className="list-item-content">
              <div className="list-item-title">
                <EditableText
                  ariaLabel={`${ariaLabel} ${item.title}`}
                  inputSelectorId={`${inputSelectorId}-${toWebSafeFormat(item.code)}-input`}
                  onChange={(newValue) => { textChangeMade(newValue, index); }}
                  content={item.title}
                  maxLength={maxLength}
                  textDisabled={!item.enabled}
                  editPermissionId={PERMISSIONS_ENUM.company.update}
                />
              </div>
              <PermissionGate permission={PERMISSIONS_ENUM.company.update}>
                <div
                  className="list-item-icon"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleEnabled(index)}
                  onKeyDown={(e) => keyDownToggleEnabled(e, index)}
                  aria-label="Toggle"
                >
                  <img src={`${getStaticCdnPath()}${item.enabled ? '/icons/eye.svg' : '/icons/eye-disabled.svg'}`} alt="" className="icon-image" />
                </div>
              </PermissionGate>
              {switchesEnabled && isPaymentMethodEnabled && (
                <div className="toggle">
                  <ToggleSwitch
                    ariaLabel={`${ariaLabel} ${item.title}`}
                    inputSelectorId={`${inputSelectorId}-${toWebSafeFormat(item.code)}-switch`}
                    onChange={(checked) => onSwitchChange(checked, index)}
                    defaultValue={item.additionalToggleValue}
                    controlled={switchesControlled}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <style jsx>
        {`
          .list {
            width: 100%;
            border-top: 1px solid var(--color-line-grey);
            display: flex;
            flex-direction: column;
          }

          .list-item {
            border-bottom: 1px solid var(--color-line-grey);
            flex-grow: 1;
          }

          .list-item-content {
            display: flex;
            flex-direction: row;
          }

          .list-item-title {
            line-height: 24px;
            width: 100%;
          }

          .list-item-icon {
            margin: auto;
          }

          .list-item-icon img {
            border-left: 1px solid var(--color-line-grey);
            padding: 0 12px;
            cursor: pointer;
          }


          .toggle {
            border-left: 1px solid var(--color-line-grey);
            margin: 20px 0;
            height: 24px;
          }
        `}
      </style>
    </>
  );
}

EditableList.defaultProps = {
  inputSelectorId: null,
  items: null,
  maxLength: null,
  switchesEnabled: false,
  switchesControlled: true,
  telemetryOnChangeEvent: null,
  telemetryToggleOffEvent: null,
  ariaLabel: null,
};

EditableList.propTypes = {
  inputSelectorId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    additionalToggleValue: PropTypes.bool,
  })),
  maxLength: PropTypes.number,
  switchesEnabled: PropTypes.bool,
  switchesControlled: PropTypes.bool,
  telemetryOnChangeEvent: PropTypes.string,
  telemetryToggleOffEvent: PropTypes.string,
  ariaLabel: PropTypes.string,
};
